import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  FolderOpen,
  Printer,
  Upload,
  CheckCircle2,
  Download,
  ChevronDown,
  FileJson,
  FileUp,
  FileText,
  Settings2,
  Link2,
  FileDown,
  History,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from './ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

function Toolbar({ project, onProjectChange, onBrowse, onExportCSV, onExportJSON, onImportJSON, onPrint, onCopyPdfLink, onCopyProjectLink, onShowVersions, saving }) {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const jsonInputRef = useRef(null);

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.match(/image\/(jpeg|jpg|png)/)) {
      toast.error('Only JPG and PNG files are allowed');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${BACKEND_URL}/api/uploads/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      onProjectChange({ ...project, logo_url: response.data.url });
      toast.success('Logo uploaded successfully');
    } catch (error) {
      console.error('Logo upload failed:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const resetWidths = () => {
    const cols = project.columns || [];
    const n = cols.length || 1;
    const w = Math.round(100 / n);
    onProjectChange({
      ...project,
      columns: cols.map((c) => ({ ...c, width: w })),
    });
    toast.success('Spaltenbreiten gleichmäßig verteilt');
  };

  return (
    <div className="no-print bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          {/* Project Info */}
          <div className="flex-1 max-w-2xl">
            <div className="flex items-center gap-3 mb-2">
              <Input
                type="text"
                value={project.name}
                onChange={(e) => onProjectChange({ ...project, name: e.target.value })}
                className="text-xl font-semibold text-slate-900 border-none shadow-none px-0 focus-visible:ring-2 focus-visible:ring-blue-600"
                placeholder="Project Name"
                data-testid="project-name-input"
              />
              {saving && (
                <span className="flex items-center gap-1 text-sm text-blue-600">
                  <CheckCircle2 className="h-4 w-4 animate-pulse" />
                  Saving...
                </span>
              )}
            </div>
            <Textarea
              value={project.notes}
              onChange={(e) => onProjectChange({ ...project, notes: e.target.value })}
              className="text-sm text-slate-600 border-none shadow-none px-0 resize-none focus-visible:ring-2 focus-visible:ring-blue-600"
              placeholder="Add project notes..."
              rows={2}
              data-testid="project-notes-input"
            />
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {/* Top Row: Primary Actions */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={onBrowse}
                data-testid="browse-projects-button"
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Browse
              </Button>

              <Button
                size="sm"
                onClick={onPrint}
                className="bg-slate-600 hover:bg-slate-700 text-white"
                data-testid="print-button"
              >
                <Printer className="h-4 w-4 mr-2" />
                Print
              </Button>
            </div>

            {/* Bottom Row: Secondary Actions */}
            <div className="flex flex-wrap gap-2">
              {/* Logo Upload */}
              <div>
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                  data-testid="logo-upload-input"
                />
                <Label htmlFor="logo-upload">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={uploadingLogo}
                    asChild
                    className="border-slate-300"
                    data-testid="upload-logo-button"
                  >
                    <span className="cursor-pointer">
                      <Upload className="h-4 w-4 mr-2" />
                      {uploadingLogo ? 'Uploading...' : 'Logo'}
                    </span>
                  </Button>
                </Label>
              </div>

              {/* Export / Import menu (selten gebraucht -> zusammengefasst) */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-slate-300"
                    data-testid="file-menu-button"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export / Import
                    <ChevronDown className="h-4 w-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem onClick={onCopyPdfLink} data-testid="copy-pdf-link-item">
                    <FileDown className="h-4 w-4 mr-2" />
                    PDF-Link kopieren
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onCopyProjectLink} data-testid="copy-project-link-item">
                    <Link2 className="h-4 w-4 mr-2" />
                    Projekt-Link kopieren
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onExportJSON} data-testid="export-json-item">
                    <FileJson className="h-4 w-4 mr-2" />
                    JSON exportieren
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => jsonInputRef.current?.click()} data-testid="import-json-item">
                    <FileUp className="h-4 w-4 mr-2" />
                    JSON importieren
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onExportCSV} data-testid="export-csv-item">
                    <FileText className="h-4 w-4 mr-2" />
                    CSV exportieren
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={onShowVersions} data-testid="versions-item">
                    <History className="h-4 w-4 mr-2" />
                    Verlauf / Rückgängig
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={resetWidths} data-testid="reset-widths-item">
                    <Settings2 className="h-4 w-4 mr-2" />
                    Spalten gleich breit
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* hidden input für JSON-Import (vom Menü getriggert) */}
              <input
                type="file"
                ref={jsonInputRef}
                accept="application/json,.json"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f && onImportJSON) onImportJSON(f);
                  e.target.value = '';
                }}
                data-testid="import-json-input"
              />
            </div>
          </div>
        </div>

        {/* Logo Preview */}
        {project.logo_url && (
          <div className="mt-3 flex items-center gap-2">
            <img
              src={`${BACKEND_URL}${project.logo_url}`}
              alt="Project Logo"
              className="h-12 object-contain"
              data-testid="logo-preview"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onProjectChange({ ...project, logo_url: '' })}
              data-testid="remove-logo-button"
            >
              Remove
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default Toolbar;
